/**
 * Singleton-Toast-Store. Eine Toast-Instanz aktiv (kein Stack — UI-SPEC §Motion).
 * Auto-Dismiss via setTimeout. Listener via useSyncExternalStore.
 */

export type ToastVariant = 'info' | 'success' | 'warning';

export interface Toast {
  readonly id: number;
  readonly message: string;
  readonly variant: ToastVariant;
}

interface State {
  active: Toast | null;
  timer: ReturnType<typeof setTimeout> | null;
  nextId: number;
  storageWarningShown: boolean;
}

const listeners = new Set<() => void>();
const state: State = {
  active: null,
  timer: null,
  nextId: 1,
  storageWarningShown: false,
};

function notify(): void {
  for (const cb of listeners) cb();
}

interface ShowOptions {
  variant?: ToastVariant;
  durationMs?: number;
}

export function showToast(message: string, opts?: ShowOptions): void {
  if (state.timer !== null) {
    clearTimeout(state.timer);
    state.timer = null;
  }
  const variant = opts?.variant ?? 'info';
  const duration = opts?.durationMs ?? 1500;
  const id = state.nextId;
  state.nextId += 1;
  state.active = { id, message, variant };
  state.timer = setTimeout(() => {
    state.active = null;
    state.timer = null;
    notify();
  }, duration);
  notify();
}

export function showStorageWarningOnce(): void {
  if (state.storageWarningShown) return;
  state.storageWarningShown = true;
  showToast(
    'Fortschritt konnte nicht gespeichert werden. Pruefe Browser-Einstellungen.',
    { variant: 'warning', durationMs: 3000 },
  );
}

export function getActiveToast(): Toast | null {
  return state.active;
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Test-Helper. NICHT im App-Code verwenden. */
export function __resetToastForTests(): void {
  if (state.timer !== null) {
    clearTimeout(state.timer);
  }
  state.active = null;
  state.timer = null;
  state.nextId = 1;
  state.storageWarningShown = false;
}
